/**
 * The level is an array of analytic primitives. The *same* data drives both the
 * collision math and the render meshes, so a client can never collide with
 * something it does not draw (or vice versa). No triangle meshes anywhere.
 *
 * Pure data — no three.js, no DOM. The server imports this file too.
 * 1 unit = 1 metre, Y up.
 */

import type { MoverSpec } from './movers';

/** A position or direction: `[x, y, z]`. */
export type V3 = [number, number, number];

/** A quaternion: `[x, y, z, w]`. */
export type V4 = [number, number, number, number];

/** Surface properties: how a primitive looks and how the pogo stick reacts to it. */
export interface Material {
  /** Base colour as `0xRRGGBB`. */
  color: number;
  /** Multiplies the bounce speed on impact. Default 1. Mushrooms use 1.9. */
  bounceMultiplier?: number;
  /** Multiplies retained tangential velocity on impact. Default 1. */
  frictionMultiplier?: number;
  /** Which kind of level feature this is — drives material reuse and level stats. */
  tag?:
    | 'plaza'
    | 'platform'
    | 'ramp'
    | 'mushroom'
    | 'pillar'
    | 'funnel'
    | 'ledge'
    | 'floor'
    | 'checkpoint'
    | 'decor'
    | 'summit'
    | 'rail'
    | 'hazard';
}

/** Oriented box. `half` is the half-extent on each local axis; `quat` rotates local → world. */
export interface BoxPrim {
  kind: 'box';
  center: V3;
  half: V3;
  quat: V4;
  material: Material;
  /** Deterministic motion, evaluated as a pure function of the shared level clock. */
  mover?: MoverSpec;
}

/** Sphere. */
export interface SpherePrim {
  kind: 'sphere';
  center: V3;
  radius: number;
  material: Material;
  /** Deterministic motion, evaluated as a pure function of the shared level clock. */
  mover?: MoverSpec;
}

/** Capped ("flat-topped") cylinder running from `a` to `b`. Landing on the cap is a real landing. */
export interface CylinderPrim {
  kind: 'cylinder';
  a: V3;
  b: V3;
  radius: number;
  material: Material;
  /** Deterministic motion, evaluated as a pure function of the shared level clock. */
  mover?: MoverSpec;
}

/** Capsule: a cylinder from `a` to `b` with hemispherical ends. */
export interface CapsulePrim {
  kind: 'capsule';
  a: V3;
  b: V3;
  radius: number;
  material: Material;
  /** Deterministic motion, evaluated as a pure function of the shared level clock. */
  mover?: MoverSpec;
}

export type Primitive = BoxPrim | SpherePrim | CylinderPrim | CapsulePrim;

/** Everything a client needs to build the course. Never sent over the wire. */
export interface LevelData {
  primitives: Primitive[];
  /** Where a fresh (or fallen) player starts. */
  spawn: V3;
  /** Y of the summit — the height the progress bar is scaled against. */
  topY: number;
  /** Top-face centres of the checkpoint pads, in climb order. */
  checkpoints: V3[];
}

/** Identity quaternion, as a fresh array (primitives own their data). */
export function identityQuat(): V4 {
  return [0, 0, 0, 1];
}
